import { Button, Card, Container, Grid, Group, Table, Text, TextInput, Title } from '@mantine/core';
import { IconBasket, IconSearch } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import DatePicker from '../../components/DatePicker';
import Metadata from '../../components/Metadata';
import Pager from '../../components/Pager/Pager';
import ProductHeadingColumns from '../../components/Products/ProductHeadingColumns';
import ProductRows from '../../components/Products/productRows';
import Spinner from '../../components/Spinner';
import PageSizeOptions from '../../constants/pageSizeOptions';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const fetchDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, filter) =>
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/products?${queryString.stringify(filter, {
        arrayFormat: 'bracket',
      })}`
    ),
  300
);

const ProductsPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const filterDefaultValues = useRef({
    from: 0,
    sortField: ['updatedAt'],
    sortOrder: ['desc'],
    size: PageSizeOptions[0],
  });
  const [filter, setFilter] = useState(filterDefaultValues.current);
  const [products, setProducts] = useState();
  const [totalCount, setTotalCount] = useState();
  const [pageCount, setPageCount] = useState();
  const [sortFieldsData, setSortFieldsData] = useState([]);

  const { accountId, domainId } = useParams();

  const newFilter = useMemo(() => {
    return { ...filterDefaultValues.current, ...queryString.parse(location.search, { arrayFormat: 'bracket' }) };
  }, [location.search]);

  const sortedFields = useMemo(() => {
    return filter.sortField.map((field, index) => ({ sortField: field, sortOrder: filter.sortOrder[index] }));
  }, [filter.sortField, filter.sortOrder]);

  useEffect(() => {
    setSortFieldsData(sortedFields);
    setFilter(newFilter);
    setProducts(undefined);
    fetchDebounced(fetch, accountId, domainId, newFilter).then(({ products, totalCount }) => {
      setProducts(products);
      setTotalCount(totalCount);
      setPageCount(Math.ceil(totalCount / filter.size));
    });
  }, [accountId, domainId, fetch, location.search, filter.size, newFilter, sortedFields]);

  const search = (e) => {
    e.preventDefault();

    const newSearch = queryString.stringify({ ...filter }, { arrayFormat: 'bracket' });
    if (location.search !== newSearch) {
      navigate({ search: newSearch });
    }
  };

  const getDate = (date) => {
    if (!date) {
      return;
    }
    return new Date(date);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFilter = { ...filter, [name]: value, from: 0 };

    setFilter(newFilter);
  };

  const handlePageClick = (e) => {
    const selectedPage = e.selected;
    const newOffset = (selectedPage - 1) * filter.size;
    if (newOffset !== filter.from) {
      navigate({
        search: queryString.stringify(
          { ...filter, from: (parseInt(selectedPage) - 1) * parseInt(filter.size) },
          { arrayFormat: 'bracket' }
        ),
      });
    }
  };

  async function setPageSize(pageSize) {
    navigate({ search: queryString.stringify({ ...filter, from: 0, size: pageSize }, { arrayFormat: 'bracket' }) });
  }

  const sortProducts = useCallback(
    (sortField, sortOrder) => {
      const sortedFields = sortFieldsData.filter((data) => data.sortField !== sortField);
      setSortFieldsData([...sortedFields, { sortField, sortOrder }]);

      const sortQueries = [...sortedFields, { sortField, sortOrder }].filter((data) => data.sortOrder !== '');
      const fields = sortQueries.map((el) => el.sortField);
      const orders = sortQueries.map((el) => el.sortOrder);
      const newFilter = { ...filter, sortField: [...fields], sortOrder: [...orders], from: 0 };
      setFilter(newFilter);
      filterDefaultValues.current = newFilter;

      navigate({ search: queryString.stringify({ ...newFilter }, { arrayFormat: 'bracket' }) });
    },
    [filter, navigate, sortFieldsData]
  );

  const searchPanel = (
    <>
      <Card withBorder mb="md">
        <Container size="lg" w="80%">
          <form onSubmit={(e) => search(e)}>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Product Id"
                  name="productId"
                  value={filter.productId || ''}
                  type="text"
                  onChange={handleChange}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Name" name="name" value={filter.name || ''} type="text" onChange={handleChange} />
              </Grid.Col>
            </Grid>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="SKU" name="sku" value={filter.sku || ''} type="text" onChange={handleChange} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Locale"
                  name="locale"
                  value={filter.locale || ''}
                  type="text"
                  onChange={handleChange}
                />
              </Grid.Col>
            </Grid>
            <Grid mb="md">
              <Grid.Col span={12}>
                <TextInput
                  label="Description"
                  name="description"
                  value={filter.description || ''}
                  type="text"
                  onChange={handleChange}
                />
              </Grid.Col>
            </Grid>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <DatePicker
                  showTimeSelect
                  prepend="Updated from"
                  placeholder="start date"
                  value={filter.updatedFrom}
                  onChange={(value) => handleChange({ target: { name: 'updatedFrom', value } })}
                  maxDate={getDate(filter.updatedTo)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <DatePicker
                  prepend="to"
                  append="local time"
                  showTimeSelect
                  placeholder="end date"
                  value={filter.updatedTo}
                  onChange={(value) => handleChange({ target: { name: 'updatedTo', value } })}
                  minDate={getDate(filter.updatedFrom)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 2 }}>
                <Group justify="flex-end">
                  <Button fullWidth type="submit" disabled={!products}>
                    <IconSearch size={14} /> Go
                  </Button>
                </Group>
              </Grid.Col>
            </Grid>
          </form>
        </Container>
      </Card>
    </>
  );
  if (!products) {
    return (
      <>
        {searchPanel}
        <Spinner />
      </>
    );
  }

  let productsTable = '';
  if (totalCount) {
    productsTable = (
      <Table striped withTableBorder highlightOnHover>
        <Table.Thead>
          <ProductHeadingColumns sortProducts={sortProducts} sortFieldsData={sortFieldsData} />
        </Table.Thead>

        <Table.Tbody>
          <ProductRows products={products} />
        </Table.Tbody>
      </Table>
    );
  }

  return (
    <>
      <Metadata title={`${Page['products']}`} />
      {searchPanel}

      <Card withBorder mb="md">
        <Card.Section inheritPadding py="md">
          {totalCount !== undefined ? (
            <Title order={5}>
              <IconBasket size={16} /> {totalCount.toLocaleString(navigator.language)} Products
            </Title>
          ) : (
            <Text>Loading...</Text>
          )}
        </Card.Section>
        <Card.Section>{productsTable}</Card.Section>
      </Card>
      <Pager
        pageSize={parseInt(filter.size)}
        pageCount={pageCount}
        onPageChange={handlePageClick}
        initialPage={Math.round(parseInt(filter.from || '0') / parseInt(filter.size) + 1)}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default ProductsPage;
